'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Spinner } from './ui/spinner';
import { type AssetPackLicenseChoice, PACKS_FOLDER_NAME, PACKS_TS_NAMESPACE, sdk } from '@/app/sdk';
const LICENSE_CHOICES: AssetPackLicenseChoice[] = ['MIT', 'Apache-2.0', 'BSD-3-Clause', 'CC0-1.0', 'CC-BY-4.0', 'Custom'];
interface NewAssetPackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absolute path to the currently-open project folder. */
  targetProjectPath: string;
}

/** Slugifies a display name into a default pack `id` (lowercase, kebab-case). */
function deriveDefaultId(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
  return /^[a-z]/.test(slug) ? slug : slug ? `pack-${slug}` : '';
}
export const NewAssetPackDialog = ({
  open,
  onOpenChange,
  targetProjectPath
}: NewAssetPackDialogProps) => {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('1.0.0');
  const [author, setAuthor] = useState('');
  const [minEngineVersion, setMinEngineVersion] = useState('0.0.0');
  const [license, setLicense] = useState<AssetPackLicenseChoice>('MIT');
  const [includeAssets, setIncludeAssets] = useState(true);
  const [includeSrc, setIncludeSrc] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveId = useMemo(() => idTouched && id ? id : deriveDefaultId(name), [id, idTouched, name]);
  const importAliasPreview = name ? `${PACKS_TS_NAMESPACE}/${name}/` : '';
  const folderPreview = name ? `${targetProjectPath}/${PACKS_FOLDER_NAME}/${name}/` : '';
  const reset = useCallback(() => {
    setName('');
    setId('');
    setIdTouched(false);
    setDescription('');
    setVersion('1.0.0');
    setAuthor('');
    setMinEngineVersion('0.0.0');
    setLicense('MIT');
    setIncludeAssets(true);
    setIncludeSrc(true);
    setSubmitting(false);
    setError(null);
  }, []);
  const handleSubmit = useCallback(async () => {
    if (!sdk?.tools.createInProjectAssetPack) {
      setError('SDK not available');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await sdk.tools.createInProjectAssetPack({
        targetProjectPath,
        name: name.trim(),
        id: effectiveId,
        description: description.trim(),
        version: version.trim(),
        author: author.trim() || undefined,
        minEngineVersion: minEngineVersion.trim(),
        license,
        includeAssets,
        includeSrc
      });
      if (result.success) {
        const folderName = result.folderPath.split(/[/\\]/).at(-1) ?? name.trim();
        toast.success(`Created pack at ${PACKS_FOLDER_NAME}/${folderName}/`);
        reset();
        onOpenChange(false);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [author, description, effectiveId, includeAssets, includeSrc, license, minEngineVersion, name, onOpenChange, reset, targetProjectPath, version]);
  return <Dialog open={open} onOpenChange={o => {
    if (!o) reset();
    onOpenChange(o);
  }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Asset Pack</DialogTitle>
          <DialogDescription>Scaffold a new pack folder under <code>{PACKS_FOLDER_NAME}/</code> in this project.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="ap-name">Name</Label>
            <Input id="ap-name" value={name} onChange={e => setName(e.target.value)} placeholder="my-asset-pack" autoFocus />
            {folderPreview && <p className="text-[11px] text-muted-foreground font-mono">{folderPreview}</p>}
            {importAliasPreview && <p className="text-[11px] text-muted-foreground font-mono">import from {importAliasPreview}</p>}
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ap-id">Id <span className="text-muted-foreground">(registry key)</span></Label>
            <Input id="ap-id" value={effectiveId} onChange={e_0 => {
            setId(e_0.target.value);
            setIdTouched(true);
          }} placeholder="my-asset-pack" />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ap-desc">Description</Label>
            <Input id="ap-desc" value={description} onChange={e_1 => setDescription(e_1.target.value)} placeholder="One-line description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label htmlFor="ap-version">Version</Label>
              <Input id="ap-version" value={version} onChange={e_2 => setVersion(e_2.target.value)} />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="ap-engine">Min Engine Version</Label>
              <Input id="ap-engine" value={minEngineVersion} onChange={e_3 => setMinEngineVersion(e_3.target.value)} />
            </div>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ap-author">Author <span className="text-muted-foreground">(optional)</span></Label>
            <Input id="ap-author" value={author} onChange={e_4 => setAuthor(e_4.target.value)} />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ap-license">License</Label>
            <Select value={license} onValueChange={v => setLicense(v as AssetPackLicenseChoice)}>
              <SelectTrigger id="ap-license"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LICENSE_CHOICES.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Pick a permissive template, or Custom to write your own.</p>
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeAssets} onChange={e_5 => setIncludeAssets(e_5.target.checked)} />
              Scaffold <code>assets/</code> folder
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeSrc} onChange={e_6 => setIncludeSrc(e_6.target.checked)} />
              Scaffold <code>src/</code> folder
            </label>
          </div>

          {error && <div className="rounded-md border border-destructive/50 bg-destructive/10 px-2 py-1 text-xs text-destructive font-mono break-all">
              {error}
            </div>}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => {
          void handleSubmit();
        }} disabled={submitting || !name.trim() || !effectiveId.trim() || !version.trim() || !minEngineVersion.trim() || !targetProjectPath}>
            {submitting ? <Spinner /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};
NewAssetPackDialog.displayName = 'NewAssetPackDialog';